/**
 * DocumentReviewTab — Queue-based document review workflow.
 *
 * Split-panel: left lists documents pending review with status filters,
 * right shows document detail with PDF preview link, compliance info,
 * and approve/reject actions.
 *
 * Backend: /api/broker-portal/executed-documents, /api/broker-portal/approve/:id
 *          /api/broker-review/document-images/:id
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClipboardCheck,
  faCheckCircle,
  faTimesCircle,
  faSpinner,
  faFile,
  faStamp,
  faEye,
  faExpand,
  faCompress,
  faExclamationTriangle,
  faUser,
} from '@fortawesome/free-solid-svg-icons';

import axiosInstance from '../../../helpers/api';
import {
  SplitPanel,
  SearchBar,
  FilterPills,
  ListItem,
  ProfileHeader,
  colors,
  fonts,
  GoldButton,
  OutlineButton,
} from '../../broker-intelligence/shared';

// ─── Types ───────────────────────────────────────────────────────────

interface ReviewDocument {
  id: string;
  filename?: string;
  original_filename?: string;
  document_type?: string;
  s3_url?: string;
  file_size?: number;
  created_at?: string;
  transaction_id?: string;
  property_address?: string;
  transaction_type?: string;
  transaction_stage?: string;
  agent_name?: string;
  agent_email?: string;
  review_status?: string;
  compliance_score?: number;
  review_notes?: string;
  reviewed_at?: string;
}

// ─── Constants ───────────────────────────────────────────────────────

const STATUS_FILTERS = [
  { key: 'all',            label: 'All' },
  { key: 'pending',        label: 'Pending' },
  { key: 'approved',       label: 'Approved' },
  { key: 'needs_revision', label: 'Needs Revision' },
  { key: 'rejected',       label: 'Rejected' },
];

const STATUS_COLORS: Record<string, string> = {
  pending:        colors.orange,
  approved:       colors.green,
  needs_revision: colors.gold,
  rejected:       colors.red,
};

const STATUS_LABELS: Record<string, string> = {
  pending:        'Pending',
  approved:       'Approved',
  needs_revision: 'Needs Revision',
  rejected:       'Rejected',
};

// The DB stores several raw values ('pending_review', '', 'needs_revision', …). Map them to the
// canonical statuses the UI filters/badges use so the queue filters actually match.
const normalizeStatus = (raw?: string | null): string => {
  const s = (raw || '').toLowerCase().trim();
  if (!s || s === 'pending' || s === 'pending_review' || s === 'in_review' || s === 'submitted') return 'pending';
  if (s === 'approved' || s === 'stamped') return 'approved';
  if (s === 'rejected' || s === 'declined') return 'rejected';
  if (s === 'needs_revision' || s === 'needs revision' || s === 'revision') return 'needs_revision';
  return s;
};

// ─── Component ───────────────────────────────────────────────────────

const DocumentReviewTab: React.FC<{ brokerageId?: string }> = ({ brokerageId }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [documents, setDocuments] = useState<ReviewDocument[]>([]);
  const [selected, setSelected] = useState<ReviewDocument | null>(null);
  const [docPreviewUrl, setDocPreviewUrl] = useState<string | null>(null); // KOPS-654: inline PDF preview (fresh URL)
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [stamping, setStamping] = useState(false); // KOPS-654: inline stamp in progress
  const [stampResult, setStampResult] = useState<{ fileNumber: string; stampDate?: string } | null>(null); // KOPS-654: stamp confirmation
  const [stampFileNumber, setStampFileNumber] = useState(''); // KOPS-654: broker-overridable file #
  const [stampDate, setStampDate] = useState(''); // KOPS-654: broker-overridable stamp date (back-dating)
  const [fullScreen, setFullScreen] = useState(false); // KOPS-654: full-screen document viewer
  const [actionMsg, setActionMsg] = useState<string | null>(null); // KOPS-654: approve/reject confirmation

  // Esc exits full-screen viewer
  useEffect(() => {
    if (!fullScreen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFullScreen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullScreen]);

  const fetchDocuments = useCallback(() => {
    setLoading(true);
    // KOPS-654: when rendered inside the Broker Intelligence Hub, the hub already knows the
    // brokerageId — pass it so a Designated Broker (who isn't a brokerage_team_members row)
    // still gets their brokerage's review queue. The status filter is applied SERVER-SIDE so
    // clicking a pill actually re-queries (and isn't limited to a client slice of recent docs).
    const params: Record<string, string> = {};
    if (brokerageId) params.brokerage_id = brokerageId;
    if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
    axiosInstance.get('/broker-portal/executed-documents', { params })
      .then(r => setDocuments(r.data?.documents || []))
      .catch(err => console.error('Failed to load documents:', err))
      .finally(() => setLoading(false));
  }, [brokerageId, statusFilter]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  // KOPS-654: load a FRESH presigned URL for a doc (the stored s3_url expires, and after a
  // stamp the backend repoints user_documents.s3_key to the stamped version — so re-fetching
  // here always returns the latest, correctly-rendering PDF).
  const loadPreview = useCallback((docId: string) => {
    setLoadingPreview(true);
    setDocPreviewUrl(null);
    axiosInstance.post('/broker-review/get-document-url', { document_id: docId })
      .then(r => setDocPreviewUrl(r.data?.url || null))
      .catch(err => { console.error('Preview URL failed:', err); setDocPreviewUrl(null); })
      .finally(() => setLoadingPreview(false));
  }, []);

  useEffect(() => {
    if (!selected?.id) { setDocPreviewUrl(null); return; }
    setStampResult(null);
    setActionMsg(null);
    setStampFileNumber('');                               // reset overrides per document
    setStampDate(new Date().toISOString().slice(0, 10));  // default stamp date = today (overridable)
    loadPreview(selected.id);
  }, [selected?.id, loadPreview]);

  // KOPS-654: deep-link — if the URL carries ?id=<documentId>, open that document once the
  // queue has loaded (lets a broker bookmark / share a link straight to the doc under review).
  useEffect(() => {
    const urlId = searchParams.get('id');
    if (!urlId || documents.length === 0 || selected?.id === urlId) return;
    const match = documents.find(d => d.id === urlId);
    if (match) { setSelected(match); setReviewNotes(''); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents]);

  // KOPS-654: reflect the document + its transaction in the address bar as the broker works,
  // so the URL always points at the actual transaction/document being processed.
  // NOTE: skip the initial mount — otherwise this would wipe an incoming ?id=&transaction= deep
  // link before the deep-link effect above can read it and open the document.
  const didMountUrlSync = useRef(false);
  useEffect(() => {
    if (!didMountUrlSync.current) { didMountUrlSync.current = true; return; }
    const next = new URLSearchParams(searchParams);
    if (selected) {
      next.set('id', selected.id);
      if (selected.transaction_id) next.set('transaction', selected.transaction_id);
      else next.delete('transaction');
    } else {
      next.delete('id');
      next.delete('transaction');
    }
    setSearchParams(next, { replace: true }); // replace → don't flood browser history per click
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  const handleReview = useCallback(async (status: 'approved' | 'rejected' | 'needs_revision') => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await axiosInstance.post(`/broker-portal/approve/${selected.id}`, {
        status,
        notes: reviewNotes,
      });
      // KOPS-654: keep the document on screen — update its status inline instead of clearing
      // the selection (which used to blank the whole panel).
      setSelected(prev => prev ? { ...prev, review_status: status } : prev);
      setActionMsg(
        status === 'approved' ? 'Document approved.'
        : status === 'rejected' ? 'Document rejected.'
        : 'Document marked as needs revision.'
      );
      fetchDocuments();
    } catch (err) {
      console.error('Review failed:', err);
    } finally {
      setSubmitting(false);
    }
  }, [selected, reviewNotes, fetchDocuments]);

  // KOPS-654: stamp inline — apply broker stamp (file # + timestamp), which also approves the doc.
  // The backend returns a fresh presigned URL of the STAMPED PDF, so we swap it straight into the
  // same iframe — the broker sees the stamped document without ever leaving the panel.
  const handleStamp = useCallback(async () => {
    if (!selected) return;
    setStamping(true);
    try {
      const res = await axiosInstance.post('/broker-review/stamp-document', {
        documentId: selected.id,
        ...(stampFileNumber.trim() ? { fileNumber: stampFileNumber.trim() } : {}),
        // send the chosen date at midday so timezone never rolls it to the previous day
        ...(stampDate ? { customDate: `${stampDate}T12:00:00` } : {}),
        ...(reviewNotes ? { notes: reviewNotes } : {}),
      });
      const stamped = res.data?.stampedDocument;
      if (res.data?.success && stamped) {
        setStampResult({ fileNumber: stamped.fileNumber, stampDate: stamped.stampDate });
        setActionMsg(null);
        setSelected(prev => prev ? { ...prev, review_status: 'approved' } : prev);
        // Re-fetch a fresh URL — the backend has repointed this doc to the stamped PDF, so this
        // reliably renders the stamped version (rather than trusting the URL echoed back).
        loadPreview(selected.id);
        fetchDocuments();                                          // refresh queue counts/status
      } else {
        console.error('Stamp failed:', res.data);
      }
    } catch (err) {
      console.error('Stamp failed:', err);
    } finally {
      setStamping(false);
    }
  }, [selected, reviewNotes, stampFileNumber, stampDate, fetchDocuments, loadPreview]);

  // Filter
  const filtered = documents.filter(d => {
    const effectiveStatus = normalizeStatus(d.review_status);
    if (statusFilter !== 'all' && effectiveStatus !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const name = (d.original_filename || d.filename || '').toLowerCase();
      const agent = (d.agent_name || '').toLowerCase();
      const addr = (d.property_address || '').toLowerCase();
      if (!name.includes(q) && !agent.includes(q) && !addr.includes(q)) return false;
    }
    return true;
  });

  const pendingCount = documents.filter(d => normalizeStatus(d.review_status) === 'pending').length;

  // ─── Left Panel ──────────────────────────────────────────────────

  const leftPanel = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '16px', borderBottom: `1px solid ${colors.navy3}` }}>
        <h3 style={{ color: colors.gold2, fontFamily: fonts.display, fontSize: 22, margin: '0 0 4px' }}>
          Document Review
        </h3>
        <p style={{ color: colors.muted, fontSize: 13, margin: '0 0 12px' }}>
          {loading
            ? 'Loading…'
            : statusFilter === 'all'
              ? `${pendingCount} pending · ${documents.length} total`
              : `${filtered.length} ${(STATUS_LABELS[statusFilter] || statusFilter).toLowerCase()}`}
        </p>
        <SearchBar value={search} onChange={setSearch} placeholder="Search documents..." />
        <div style={{ marginTop: 8 }}>
          <FilterPills options={STATUS_FILTERS} activeKey={statusFilter} onChange={setStatusFilter} />
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: colors.muted }}>
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: colors.muted }}>
            No documents found
          </div>
        ) : filtered.map(d => {
          const status = normalizeStatus(d.review_status);
          return (
            <ListItem
              key={d.id}
              selected={selected?.id === d.id}
              onClick={() => { setSelected(d); setReviewNotes(''); }}
              title={d.original_filename || d.filename || 'Untitled Document'}
              subtitle={`${d.agent_name || 'Unknown Agent'} | ${d.property_address || 'No address'}`}
              rightContent={
                <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: STATUS_COLORS[status] || colors.muted, color: '#fff' }}>
                  {STATUS_LABELS[status] || status}
                </span>
              }
            />
          );
        })}
      </div>
    </div>
  );

  // ─── Right Panel ─────────────────────────────────────────────────

  const rightPanel = selected ? (
    <div style={{ padding: 24, flex: 1, minHeight: 0, overflowY: 'auto' }}>
      <ProfileHeader
        title={selected.original_filename || selected.filename || 'Document'}
        metaItems={[
          { icon: faUser, text: `Agent: ${selected.agent_name || 'N/A'}` },
          { icon: faFile, text: selected.property_address || 'No address' },
        ]}
        badges={
          <>
            <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: STATUS_COLORS[normalizeStatus(selected.review_status)] || colors.muted, color: '#fff' }}>
              {STATUS_LABELS[normalizeStatus(selected.review_status)] || normalizeStatus(selected.review_status)}
            </span>
            {selected.document_type && (
              <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: colors.blue, color: '#fff', marginLeft: 6 }}>
                {selected.document_type}
              </span>
            )}
          </>
        }
      />

      {/* KOPS-654: inline document preview — the broker reads the PDF right here (no modal popup) */}
      <div style={{ marginTop: 20, background: colors.white, borderRadius: 8, border: `1px solid ${colors.border}`, overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h4 style={{ margin: 0, fontFamily: fonts.display, fontSize: 18 }}>Document Preview</h4>
          {docPreviewUrl && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <button
                onClick={() => setFullScreen(true)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.gold, fontWeight: 600, fontSize: 13, padding: 0, fontFamily: fonts.body }}
              >
                <FontAwesomeIcon icon={faExpand} style={{ marginRight: 6 }} />Full screen
              </button>
              <a href={docPreviewUrl} target="_blank" rel="noopener noreferrer" style={{ color: colors.gold, textDecoration: 'none', fontWeight: 600, fontSize: 13 }}>
                <FontAwesomeIcon icon={faEye} style={{ marginRight: 6 }} />Open in new tab
              </a>
            </div>
          )}
        </div>
        <div style={{ height: '70vh', background: '#f5f5f5' }}>
          {loadingPreview ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: colors.muted }}>
              <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} />Loading document…
            </div>
          ) : docPreviewUrl ? (
            <iframe key={docPreviewUrl} src={docPreviewUrl} title="Document preview" width="100%" height="100%" style={{ border: 'none', display: 'block' }} />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: colors.muted, flexDirection: 'column', gap: 8 }}>
              <FontAwesomeIcon icon={faFile} style={{ fontSize: 28, opacity: 0.3 }} />
              Preview unavailable for this document
            </div>
          )}
        </div>
      </div>

      {/* KOPS-654: full-screen document viewer — hides all the review chrome so the broker reads the doc edge-to-edge */}
      {fullScreen && docPreviewUrl && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: colors.navy, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderBottom: `1px solid ${colors.navy3}`, flexShrink: 0 }}>
            <span style={{ color: colors.gold2, fontFamily: fonts.display, fontSize: 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 16 }}>
              {selected.original_filename || selected.filename || 'Document'}
            </span>
            <button
              onClick={() => setFullScreen(false)}
              style={{ background: colors.gold, color: colors.navy, border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: fonts.body, flexShrink: 0 }}
            >
              <FontAwesomeIcon icon={faCompress} style={{ marginRight: 8 }} />Hide — Exit Full Screen
            </button>
          </div>
          <iframe key={docPreviewUrl} src={docPreviewUrl} title="Document full screen" style={{ flex: 1, width: '100%', border: 'none', background: '#f5f5f5' }} />
        </div>
      )}

      <div style={{ marginTop: 20 }}>
        {/* Document Info */}
        <div style={{ background: colors.white, borderRadius: 8, padding: 20, border: `1px solid ${colors.border}`, marginBottom: 16 }}>
          <h4 style={{ margin: '0 0 16px', fontFamily: fonts.display, fontSize: 18 }}>Document Details</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><span style={{ color: colors.muted, fontSize: 12 }}>Type</span><br />{selected.document_type || 'Unknown'}</div>
            <div><span style={{ color: colors.muted, fontSize: 12 }}>Stage</span><br />{selected.transaction_stage || 'N/A'}</div>
            <div><span style={{ color: colors.muted, fontSize: 12 }}>Uploaded</span><br />{selected.created_at ? new Date(selected.created_at).toLocaleDateString() : 'N/A'}</div>
            <div><span style={{ color: colors.muted, fontSize: 12 }}>Compliance Score</span><br />{selected.compliance_score != null ? `${selected.compliance_score}%` : 'Pending'}</div>
          </div>
          {selected.transaction_id && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${colors.border}` }}>
              <span style={{ color: colors.muted, fontSize: 12 }}>Transaction</span><br />
              <a
                href={`/broker-intelligence/transactions?id=${selected.transaction_id}`}
                style={{ color: colors.gold, textDecoration: 'none', fontWeight: 600, fontSize: 13 }}
              >
                <FontAwesomeIcon icon={faEye} style={{ marginRight: 6 }} />
                Open transaction {selected.property_address ? `— ${selected.property_address}` : `#${selected.transaction_id.slice(0, 8)}`}
              </a>
            </div>
          )}
        </div>

        {/* Previous Review Notes */}
        {selected.review_notes && (
          <div style={{ background: colors.white, borderRadius: 8, padding: 20, border: `1px solid ${colors.border}`, marginBottom: 16 }}>
            <h4 style={{ margin: '0 0 12px', fontFamily: fonts.display, fontSize: 18 }}>Previous Review</h4>
            <p style={{ margin: 0, color: '#333' }}>{selected.review_notes}</p>
            {selected.reviewed_at && (
              <p style={{ margin: '8px 0 0', fontSize: 12, color: colors.muted }}>
                Reviewed: {new Date(selected.reviewed_at).toLocaleString()}
              </p>
            )}
          </div>
        )}

        {/* Review Actions — KOPS-654: fully inline (review → approve/reject → stamp), no modal */}
        <div style={{ background: colors.white, borderRadius: 8, padding: 20, border: `1px solid ${colors.border}` }}>
          <h4 style={{ margin: '0 0 16px', fontFamily: fonts.display, fontSize: 18 }}>Review Action</h4>

          {/* Approve / reject confirmation */}
          {actionMsg && !stampResult && (
            <div style={{ background: colors.offBg, border: `1px solid ${colors.border}`, borderRadius: 6, padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
              <FontAwesomeIcon icon={faCheckCircle} style={{ color: colors.green }} />
              <span style={{ fontSize: 13, color: colors.navy }}>{actionMsg}</span>
            </div>
          )}

          {/* Stamp confirmation banner */}
          {stampResult && (
            <div style={{ background: '#f0f7f0', border: `1px solid ${colors.green}`, borderRadius: 6, padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
              <FontAwesomeIcon icon={faCheckCircle} style={{ color: colors.green }} />
              <span style={{ fontSize: 13, color: colors.navy }}>
                Stamped &amp; approved — File <b>#{stampResult.fileNumber}</b>
                {stampResult.stampDate ? ` on ${stampResult.stampDate}` : ''}. The stamped PDF is shown above.
              </span>
            </div>
          )}

          <textarea
            value={reviewNotes}
            onChange={e => setReviewNotes(e.target.value)}
            placeholder="Add review notes (optional)..."
            style={{
              width: '100%',
              minHeight: 72,
              padding: 12,
              borderRadius: 6,
              border: `1px solid ${colors.border}`,
              fontFamily: fonts.body,
              fontSize: 14,
              resize: 'vertical',
              marginBottom: 16,
            }}
          />

          {/* KOPS-654: broker sets/overrides the File # and Date that get stamped (e.g. back-dating) */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 12, color: colors.muted, marginBottom: 4 }}>File #</label>
              <input
                type="text"
                value={stampFileNumber}
                onChange={e => setStampFileNumber(e.target.value)}
                placeholder="Auto-generated if blank"
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${colors.border}`, fontFamily: fonts.body, fontSize: 14, boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 12, color: colors.muted, marginBottom: 4 }}>Stamp Date</label>
              <input
                type="date"
                value={stampDate}
                onChange={e => setStampDate(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${colors.border}`, fontFamily: fonts.body, fontSize: 14, boxSizing: 'border-box' }}
              />
            </div>
          </div>

          {/* Primary path — apply the broker stamp (file # + date); this also approves the doc */}
          <GoldButton
            onClick={handleStamp}
            disabled={stamping || submitting}
            style={{ width: '100%', marginBottom: 8, opacity: stamping ? 0.7 : 1 }}
          >
            <FontAwesomeIcon icon={stamping ? faSpinner : faStamp} spin={stamping} style={{ marginRight: 8 }} />
            {stamping ? 'Stamping…' : 'Approve & Stamp Document'}
          </GoldButton>
          <div style={{ fontSize: 12, color: colors.muted, marginBottom: 16 }}>
            Applies your broker stamp with a file number and timestamp, marks the document approved, and re-renders the stamped PDF above.
          </div>

          {/* Secondary actions */}
          <div style={{ display: 'flex', gap: 12 }}>
            <OutlineButton onClick={() => handleReview('approved')} disabled={submitting || stamping}>
              <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: 6 }} />
              Approve (no stamp)
            </OutlineButton>
            <OutlineButton onClick={() => handleReview('needs_revision')} disabled={submitting || stamping}>
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: 6 }} />
              Needs Revision
            </OutlineButton>
            <button
              onClick={() => handleReview('rejected')}
              disabled={submitting || stamping}
              style={{
                padding: '8px 20px',
                borderRadius: 6,
                border: `1px solid ${colors.red}`,
                background: 'transparent',
                color: colors.red,
                fontWeight: 600,
                cursor: (submitting || stamping) ? 'not-allowed' : 'pointer',
                fontFamily: fonts.body,
              }}
            >
              <FontAwesomeIcon icon={faTimesCircle} style={{ marginRight: 6 }} />
              Reject
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: colors.muted }}>
      <div style={{ textAlign: 'center' }}>
        <FontAwesomeIcon icon={faClipboardCheck} size="3x" style={{ marginBottom: 16, opacity: 0.3 }} />
        <p>Select a document to review</p>
      </div>
    </div>
  );

  return <SplitPanel left={leftPanel} right={rightPanel} />;
};

export default DocumentReviewTab;
